import { z } from 'zod';

import {
  PolaritySchema,
  ThemeContentAxisSchema,
  ThemeSeverityHintSchema,
  ThemeStatusSchema,
} from './aspect-theme.js';

// Schemas for the studio-facing Insights surface. Three RPCs back the page:
//   - get_game_themes (themes list)
//   - get_theme_detail (theme + aspect evidence)
//   - get_game_channel_snapshots (per-channel snapshot status)
// And one realtime broadcast topic insights:<gameId> with a slim payload.
// All defined in migration 0076. Shared enums (Polarity, ThemeStatus,
// ThemeSeverityHint, ThemeContentAxis) are reused from aspect-theme.ts.

// --- Bucket (Pass C classification) ----------------------------------------

export const ThemeBucketSchema = z.enum([
  'emerging',
  'trending',
  'standout',
  'critical',
  'none',
]);
export type ThemeBucket = z.infer<typeof ThemeBucketSchema>;

// Trajectory: the most-recent prior bucket on record (if any) for this
// theme. Both fields are null on a theme that has never transitioned
// (first classification, or no history rows yet).
export const ThemeTrajectorySchema = z.object({
  previousBucket: ThemeBucketSchema.nullable(),
  previousClassifiedAt: z.string().datetime({ offset: true }).nullable(),
});
export type ThemeTrajectory = z.infer<typeof ThemeTrajectorySchema>;

// --- get_game_themes row ----------------------------------------------------

export const ThemeListItemSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  dominantPolarity: PolaritySchema,
  severityHint: ThemeSeverityHintSchema.nullable(),
  contentAxis: ThemeContentAxisSchema,
  status: ThemeStatusSchema,
  pinned: z.boolean(),
  bucket: ThemeBucketSchema,
  trajectory: ThemeTrajectorySchema,
  firstSeenAt: z.string().datetime({ offset: true }),
  // Oldest member's posted_at (server falls back to captured_at, then
  // first_seen_at). Drives the "first seen" label so backfilled patterns
  // read from the true earliest mention rather than the clustering time.
  // Nullable so payloads predating migration 0115 still parse.
  earliestMentionAt: z.string().datetime({ offset: true }).nullable(),
  lastMemberAddedAt: z.string().datetime({ offset: true }).nullable(),
  memberCount: z.number().int().nonnegative(),
  // Distinct discord_channel_name values touched by this theme's
  // active aspects (up to 10). Empty array if none / not from chat.
  channelNames: z.array(z.string()),
});
export type ThemeListItem = z.infer<typeof ThemeListItemSchema>;

// --- get_theme_detail -------------------------------------------------------

// One aspect-evidence row joined to feedback_pool. Source fields are
// nullable to tolerate deletes and the rare row without posted_at.
export const ThemeAspectEvidenceSchema = z.object({
  id: z.string().uuid(),
  textSpan: z.string(),
  polarity: PolaritySchema,
  confidence: z.number().min(0).max(1),
  extractedAt: z.string().datetime({ offset: true }),
  feedbackRecordId: z.string().uuid(),
  feedbackRawText: z.string().nullable(),
  feedbackSource: z.string(),
  feedbackPostedAt: z.string().datetime({ offset: true }).nullable(),
  feedbackAuthorExternalId: z.string().nullable(),
  feedbackAuthorHandle: z.string().nullable(),
  feedbackChannelName: z.string().nullable(),
  // Pre-formatted display label for the mention row chip:
  // `✎ <form>` for form-submission rows, `↪ <forum>` for forum-type
  // channels, `# <channel>` for everything else. Computed server-side
  // in get_theme_detail (migrations 0087, 0090) so thread rows show
  // the parent forum name and form rows show the form's own title
  // rather than the channel the form happens to live in.
  feedbackChannelLabel: z.string().nullable(),
  reactionCount: z.number().int().nonnegative(),
  // Routing hints for "View in feed". Only one of these is set per
  // row: poll mentions carry pollId, playtest mentions carry the
  // playtest pair, every other source carries none. Optional so older
  // snapshots / cached responses without these keys keep parsing.
  // Wired up in migration 0096.
  pollId: z.string().uuid().nullable().optional(),
  playtestId: z.string().uuid().nullable().optional(),
  playtestSessionId: z.string().uuid().nullable().optional(),
});
export type ThemeAspectEvidence = z.infer<typeof ThemeAspectEvidenceSchema>;

export const ThemeDetailSchema = z.object({
  theme: ThemeListItemSchema,
  aspects: z.array(ThemeAspectEvidenceSchema),
});
export type ThemeDetail = z.infer<typeof ThemeDetailSchema>;

// --- get_game_channel_snapshots row ----------------------------------------

export const ChannelSnapshotStatusSchema = z.object({
  discordChannelId: z.string(),
  channelName: z.string().nullable(),
  lastDoneAt: z.string().datetime({ offset: true }).nullable(),
  windowStart: z.string().datetime({ offset: true }).nullable(),
  windowEnd: z.string().datetime({ offset: true }).nullable(),
  aspectCount: z.number().int().nullable(),
  attachedCount: z.number().int().nullable(),
  newThemeCount: z.number().int().nullable(),
  inFlight: z.boolean(),
});
export type ChannelSnapshotStatus = z.infer<typeof ChannelSnapshotStatusSchema>;

// --- Realtime broadcast payload --------------------------------------------

// Payloads broadcast on the insights:<gameId> topic. Two emitters:
//   - notify_insights_snapshot_changed (0076/0117): per-channel snapshot
//     lifecycle - 'running' (pending->claimed), 'done', 'dead'. Carries the
//     channel id.
//   - broadcast_insights_themes_updated (0119): per-game terminal signal once
//     the post-snapshot theme re-attach + bucket recompute settles. Has no
//     channel id, hence discordChannelId is optional.
export const InsightsBroadcastPayloadSchema = z.object({
  discordChannelId: z.string().optional(),
  status: z.enum(['running', 'done', 'themes_updated', 'dead']),
});
export type InsightsBroadcastPayload = z.infer<typeof InsightsBroadcastPayloadSchema>;
